/**
 * /cli/authorize — 「この端末の Kumo CLI を許可」（2026-09-28 creator/connect・Core の cli-grants bb8a83f2 の依頼 ②）。
 * `xkumo connect` / `xkumo login`（フラグなし）がブラウザでここを開く。往復・値の検査・WHY は ../cliAuthorize.ts。
 *
 * 出すもの（指示書「端末の名前・時刻・自分で connect / login を実行していなければ押さない・メール未確認なら案内」）:
 *   - どのアカウントで許可するか（メール）・端末の名前（CLI の申告 = 偽れるので「CLI が名乗った名前」と書く）・今の時刻
 *   - 目立つ警告: 自分でいま実行していなければ押さない（他人が作ったリンクを踏まされた場合に備える）
 *   - 許可 → サーバーの応答の 127.0.0.1 の戻り先へ移る（組み立てない）/ やめる → 同じ戻り先に error=access_denied
 *   - メール未確認: 許可のボタンを出さず、確認の案内と再送（サーバーも 403 email_unverified で断る）
 * ログインしていなければ、App.tsx のログインの木が /login へ移し、ログイン後にこの URL（クエリ込み）へ戻す。
 *
 * WHY 自動で許可しないか: 押す = 全権のセッション（kds_）をこの端末の CLI に渡す。本人が「いま自分で実行した」と確かめる 1 回が要る。
 * WHY 押した後にボタンを消すか: code は一回限り（60 秒）。二度押しで 2 つ目の code を作らない。
 */
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api, ApiError } from '../api';
import { CLI } from '../cliCommand';
import { cliDenyUrl, cliRedirectUrl, isCliCallback, parseCliAuthorize, type CliAuthorizeRequest } from '../cliAuthorize';
import { EmailUnverifiedNotice } from '../components/EmailUnverified';
import { fullDate } from '../components/ui';
import { errorText, useT } from '../i18n';
import { useSession } from '../session';
import { AuthShell } from './AccountRecovery';

export function CliAuthorizePage() {
  const { t, tr } = useT();
  const location = useLocation();
  const req = useMemo(() => parseCliAuthorize(location.search), [location.search]);

  if (!req) {
    return (
      <AuthShell title={t('cliAuth.invalidTitle')}>
        <p>{tr('cliAuth.invalidBody', { login: <code>{CLI} login</code>, connect: <code>{CLI} connect</code> })}</p>
      </AuthShell>
    );
  }
  return <Authorize req={req} />;
}

function Authorize({ req }: { req: CliAuthorizeRequest }) {
  const { t, tr } = useT();
  const { developer } = useSession();
  // 開いた時刻（画面の表示だけ）。描き直しで変わらないよう最初の 1 回に固定
  const [openedAt] = useState(() => new Date().toISOString());
  const [state, setState] = useState<'idle' | 'busy' | 'allowed' | 'cancelled'>('idle');
  const [error, setError] = useState<ApiError | null>(null);
  const unverified = developer?.emailVerified === false || error?.code === 'email_unverified';

  const allow = async () => {
    setState('busy');
    setError(null);
    try {
      const res = await api<{ redirectUrl?: unknown }>('/v1/developers/cli-grants', { method: 'POST', body: { challenge: req.challenge, redirectUrl: cliRedirectUrl(req) } });
      if (!isCliCallback(res.redirectUrl, req)) throw new ApiError(0, 'unexpected_redirect', 'The server returned an unexpected redirect');
      setState('allowed');
      window.location.assign(res.redirectUrl);
    } catch (err) {
      setState('idle');
      setError(err as ApiError);
    }
  };

  const cancel = () => {
    setState('cancelled');
    window.location.assign(cliDenyUrl(req));
  };

  if (state === 'allowed' || state === 'cancelled') {
    return (
      <AuthShell title={t(state === 'allowed' ? 'cliAuth.allowedTitle' : 'cliAuth.cancelledTitle')}>
        <p role="status">{t(state === 'allowed' ? 'cliAuth.allowedBody' : 'cliAuth.cancelledBody')}</p>
      </AuthShell>
    );
  }

  // 並び（2026-10-03 console/plain-confirm・founder「今何しているかわかりずらすぎる」）: 見出し 1 文 → 結果 1 行 → ボタン 2 つ →
  // 安全の 1 行 → ログイン中のアカウント（小さく）→「詳細」（端末の名前・時刻・コマンドの名前の警告・許可で何ができるか）。
  // 押せる条件（メール未確認なら「許可」を出さない）・行き先の検査（isCliCallback）は以前のまま
  return (
    <AuthShell title={t('cliAuth.title')}>
      <p className="confirm-plain-result">{t('cliAuth.lead')}</p>
      {unverified ? (
        <>
          <EmailUnverifiedNotice />
          <p className="small muted">{t('cliAuth.unverifiedThen')}</p>
        </>
      ) : null}
      {error && !unverified && (
        <p className="alert alert-error" role="alert">
          {error.code === 'unexpected_redirect' ? t('cliAuth.unexpectedRedirect') : errorText(error)}
        </p>
      )}
      <div className="confirm-plain-actions">
        {!unverified && (
          <button type="button" className="btn btn-primary confirm-plain-btn" onClick={() => void allow()} disabled={state === 'busy'}>
            {state === 'busy' ? t('common.working') : t('cliAuth.allow')}
          </button>
        )}
        <button type="button" className="btn confirm-plain-btn" onClick={cancel} disabled={state === 'busy'}>
          {t('cliAuth.cancel')}
        </button>
      </div>
      <p className="confirm-plain-safety" role="note">
        <strong>{t('cliAuth.safety')}</strong>
      </p>
      {/* クラウドで動く CLI（2026-10-07 creator/remote-login・packages/cli の commands/cliLogin.ts）: 押すとブラウザは 127.0.0.1 の戻り先へ移るが、
          CLI が利用者の PC に無いと「接続できない」になる。そのときアドレス欄の URL を CLI（か AI の会話）に貼れば続く、を押す前に 1 行。
          WHY 移る URL をこの画面に出してコピーさせないか: その URL には一回限りの code が入る。画面に出すと「その URL を送って」と頼まれたときに
          渡しやすくなる（code を換えられるのは CLI の verifier を持つ側 = 他人が作ったリンクなら他人）。アドレス欄に出るのは押した直後の
          そのタブだけで、ふだんは自動で CLI に届く（貼る必要が無い）ので、案内は文だけにする */}
      <p className="small muted">{t('cliAuth.pasteHint')}</p>
      {/* 利用者が決めた値（メール・端末の名前）は文言に混ぜず、テキストとして別の要素に出す */}
      <p className="small muted">{tr('cliAuth.signedInAs', { email: <strong>{developer?.email ?? ''}</strong> })}</p>
      <details className="confirm-plain-details">
        <summary>{t('confirmPlain.details')}</summary>
        <dl className="cli-auth-facts">
          <dt>{t('cliAuth.device')}</dt>
          <dd>{req.device ?? t('cliAuth.deviceUnknown')}</dd>
          <dt>{t('cliAuth.time')}</dt>
          <dd>{fullDate(openedAt)}</dd>
        </dl>
        <p className="alert alert-warn" role="note">
          <strong>{tr('cliAuth.warning', { connect: <code>{CLI} connect</code>, login: <code>{CLI} login</code> })}</strong>
        </p>
        <p className="small muted">{t('cliAuth.grants')}</p>
      </details>
    </AuthShell>
  );
}
